import React, { useMemo } from "react";
import { Box } from "@mui/material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import { groupTopChartItems } from "../../utils/chartData";
import DashboardChartLegend from "./DashboardChartLegend";
import DashboardGroupedChartHint from "./DashboardGroupedChartHint";

/**
 * Pie chart tuned for dashboards: no overlapping slice labels, top-N grouping, scrollable legend.
 */
export default function DashboardPieChartView({
  data = [],
  series = [],
  height = 300,
  maxSlices = 8,
  othersLabel = "Outros",
  tooltipContent,
  legendMaxHeight = 96,
  getFill = (i, item) => item?.fill || series[i % series.length],
}) {
  const prepared = useMemo(
    () =>
      groupTopChartItems(data, {
        maxItems: maxSlices,
        valueKey: "value",
        nameKey: "name",
        othersLabel,
      }),
    [data, maxSlices, othersLabel]
  );

  const colored = useMemo(
    () =>
      prepared.items.map((item, i) => ({
        ...item,
        fill: getFill(i, item),
      })),
    [prepared.items, getFill]
  );

  if (!colored.length) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height }}>
      <DashboardGroupedChartHint
        shown={colored.length}
        total={prepared.totalCount}
      />
      <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={colored}
              dataKey="value"
              nameKey="name"
              outerRadius="78%"
              label={false}
              isAnimationActive={colored.length <= 12}
            >
              {colored.map((d, i) => (
                <Cell key={`${d.name}-${i}`} fill={d.fill} />
              ))}
            </Pie>
            <Tooltip content={tooltipContent} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <DashboardChartLegend
        items={colored}
        getColor={(i, item) => item?.fill || series[i % series.length]}
        maxHeight={legendMaxHeight}
      />
    </Box>
  );
}
